import React from "react";

/* ---------------------------------------------------------------------------
   Without one of these, a single component throwing during render unmounts the
   entire tree: React clears the root and the operator gets a white page with
   nothing in it - no message, no clue which screen failed, and no way back.
   That is a worse failure than whatever threw.

   So: catch it, keep the rest of the app reachable, and say what happened. The
   component stack is the part that matters when somebody reports "it just went
   blank", and in a production build it is the only thing that names the screen.
--------------------------------------------------------------------------- */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, stack: "" };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const stack = (info && info.componentStack) || "";
    this.setState({ stack });
    // Left on the console on purpose: a screenshot of it is usually all the
    // report anybody sends.
    console.error("Screen failed to render:", error, stack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    const first = (this.state.stack || "").trim().split("\n")[0] || "";
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0B0F19] p-6">
        <div className="w-full max-w-lg rounded-2xl border border-rose-200 dark:border-rose-900/50 bg-white dark:bg-slate-900 p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900 dark:text-white">This screen could not be shown</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            The rest of the app still works - go back and try another page.
          </p>
          <p className="mt-4 text-xs font-mono text-rose-600 dark:text-rose-400 break-words">
            {String(this.state.error)}
          </p>
          {first && (
            <p className="mt-1 text-xs font-mono text-slate-400 break-words">in{first}</p>
          )}
          <div className="mt-5 flex gap-2">
            <button onClick={() => { this.setState({ error: null, stack: "" }); window.history.back(); }}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
              Go back
            </button>
            <button onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium">
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
